import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { X, Send, Sparkles, AlertTriangle, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { getAvatarColor, getInitials } from '@/lib/utils/avatarUtils'
import {
  authRepo,
  aiRepo,
  profileRepo,
  CONVERSATION_MESSAGE_LIMIT,
} from '@/lib/repositories'
import { RateLimitError } from '@/lib/repositories/supabase/SupabaseAiRepository'
import { useVisualViewportHeight } from '@/hooks/useVisualViewportHeight'
import { useTranslation } from 'react-i18next'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

interface AITradingChatProps {
  open: boolean
  onClose: () => void
  insightId?: string
  ticker?: string
  sentiment?: string
  content?: string
  resumeConversationId?: string
}

const MAX_MESSAGE_LENGTH = 1000

interface Message {
  id: string
  type: 'ai' | 'user'
  content: string
  timestamp: Date
}

export function AITradingChat({
  open,
  onClose,
  insightId,
  ticker,
  sentiment,
  content,
  resumeConversationId,
}: AITradingChatProps) {
  const { t } = useTranslation()
  const isGeneralMode = !insightId || insightId === 'general'
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [userAvatar, setUserAvatar] = useState({ initials: '', color: '' })
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [userMessageCount, setUserMessageCount] = useState(0)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const initializedRef = useRef(false)

  const isNearConversationLimit =
    userMessageCount >= CONVERSATION_MESSAGE_LIMIT - 5
  const isAtConversationLimit = userMessageCount >= CONVERSATION_MESSAGE_LIMIT

  const { height: viewportHeight, keyboardVisible } =
    useVisualViewportHeight(open)

  const handleNewChat = useCallback(async () => {
    try {
      const user = await authRepo.getCurrentUser()
      if (!user) return

      const effectiveInsightId = isGeneralMode ? 'general' : insightId!
      const newConvId = await aiRepo.createNewConversation(
        effectiveInsightId,
        user.id
      )
      setConversationId(newConvId)
      analytics.capture(AnalyticsEvents.AI_CHAT_CONVERSATION_CREATED, {
        conversation_type: isGeneralMode ? 'general' : 'insight',
        ticker,
      })
      setUserMessageCount(0)
      setInput('')

      const greetingContent = isGeneralMode
        ? t('aiGreetingGeneral')
        : t('aiGreetingInsight', {
            sentiment,
            ticker: ticker ? ` on $${ticker}` : '',
          })
      setMessages([
        {
          id: 'greeting',
          type: 'ai',
          content: greetingContent,
          timestamp: new Date(),
        },
      ])
      aiRepo.saveMessage(newConvId, 'assistant', greetingContent)

      toast.success(t('newChatStarted'), {
        description: t('previousChatInHistory'),
      })
    } catch (err) {
      console.error('Failed to start new chat:', err)
    }
  }, [isGeneralMode, insightId, sentiment, ticker, t])

  // Load user avatar data
  useEffect(() => {
    async function loadUserAvatar() {
      try {
        const user = await authRepo.getCurrentUser()
        const username = user?.username || 'user'
        const profile = await profileRepo.getProfile(username)
        const displayName = profile?.displayName || username

        setUserAvatar({
          initials: getInitials(displayName, username),
          color: getAvatarColor(username),
        })
      } catch {
        setUserAvatar({
          initials: 'U',
          color: 'from-emerald-500 to-cyan-500',
        })
      }
    }

    if (open) {
      loadUserAvatar()
    }
  }, [open])

  // Initialize conversation and load history when sheet opens
  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      setConversationId(null)
      setMessages([])
      setInput('')
      setIsTyping(false)
      setUserMessageCount(0)
      return
    }

    if (initializedRef.current) return
    initializedRef.current = true

    analytics.capture(AnalyticsEvents.AI_CHAT_OPENED, {
      conversation_type: isGeneralMode ? 'general' : 'insight',
      ticker,
    })

    let cancelled = false

    async function initConversation() {
      try {
        const user = await authRepo.getCurrentUser()
        if (!user || cancelled) return

        const effectiveInsightId = isGeneralMode ? 'general' : insightId!
        setIsLoadingHistory(true)

        const convId =
          resumeConversationId ||
          (await aiRepo.getOrCreateConversation(effectiveInsightId, user.id))
        if (cancelled) return
        setConversationId(convId)

        const history = await aiRepo.getConversationMessages(convId)
        if (cancelled) return

        const userMsgCount = history.filter((m) => m.role === 'user').length
        setUserMessageCount(userMsgCount)

        if (history.length > 0) {
          const restored: Message[] = history.map((m) => ({
            id: m.id,
            type: m.role === 'user' ? 'user' : 'ai',
            content: m.content,
            timestamp: new Date(m.createdAt),
          }))
          setMessages(restored)
        } else {
          const greetingContent = isGeneralMode
            ? t('aiGreetingGeneral')
            : t('aiGreetingInsight', {
                sentiment,
                ticker: ticker ? ` on $${ticker}` : '',
              })
          setMessages([
            {
              id: 'greeting',
              type: 'ai',
              content: greetingContent,
              timestamp: new Date(),
            },
          ])
          aiRepo.saveMessage(convId, 'assistant', greetingContent)
        }
      } catch (err) {
        if (cancelled) return
        console.error('Failed to init conversation:', err)
        const greetingContent = isGeneralMode
          ? t('aiGreetingGeneral')
          : t('aiGreetingInsight', {
              sentiment,
              ticker: ticker ? ` on $${ticker}` : '',
            })
        setMessages([
          {
            id: 'greeting',
            type: 'ai',
            content: greetingContent,
            timestamp: new Date(),
          },
        ])
      } finally {
        if (!cancelled) setIsLoadingHistory(false)
      }
    }

    initConversation()
    return () => {
      cancelled = true
    }
  }, [
    open,
    insightId,
    isGeneralMode,
    sentiment,
    ticker,
    resumeConversationId,
    t,
  ])

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, isLoadingHistory, viewportHeight, scrollToBottom])

  const handleSend = async () => {
    if (!input.trim() || isAtConversationLimit) return

    try {
      const userMessage: Message = {
        id: Date.now().toString(),
        type: 'user',
        content: input.trim(),
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])
      setUserMessageCount((prev) => prev + 1)
      const userInput = input
      setInput('')
      setIsTyping(true)
      analytics.capture(AnalyticsEvents.AI_CHAT_MESSAGE_SENT, {
        conversation_type: isGeneralMode ? 'general' : 'insight',
        ticker,
      })

      if (conversationId) {
        aiRepo.saveMessage(conversationId, 'user', userInput)
      }

      const response = await aiRepo.sendMessage(
        isGeneralMode ? 'general' : insightId!,
        userInput,
        isGeneralMode ? {} : { ticker, sentiment, content },
        conversationId || undefined
      )

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response.message,
        timestamp: response.timestamp,
      }

      setMessages((prev) => [...prev, aiResponse])

      if (conversationId) {
        aiRepo.saveMessage(conversationId, 'assistant', response.message)
      }
    } catch (error) {
      console.error('AI request failed:', error)

      let errorContent = t('aiError')
      if (error instanceof RateLimitError) {
        errorContent = t('aiRateLimit', {
          seconds: error.retryAfter,
          limit: error.limit,
        })
      }

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: errorContent,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsTyping(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const charsRemaining = MAX_MESSAGE_LENGTH - input.length
  const isNearLimit = charsRemaining <= 100
  const isAtLimit = charsRemaining <= 0

  const charCountColor = useMemo(() => {
    if (isAtLimit) return 'text-red-400'
    if (isNearLimit) return 'text-amber-400'
    return 'text-neutral-600'
  }, [isAtLimit, isNearLimit])

  const sentimentColor =
    {
      bullish: 'text-emerald-400',
      bearish: 'text-red-400',
      neutral: 'text-neutral-400',
    }[(sentiment || 'neutral').toLowerCase()] || 'text-neutral-400'

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        className="bg-neutral-950 border-white/[0.06] flex flex-col max-h-[90dvh] w-full sm:max-w-md p-0"
        style={{
          ...(viewportHeight
            ? {
                height: `${viewportHeight}px`,
                maxHeight: `${viewportHeight}px`,
              }
            : {}),
          ...(keyboardVisible
            ? { top: 0, bottom: 'auto', marginTop: 0 }
            : {}),
        }}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between p-5 border-b border-white/[0.06] bg-black/50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Sparkles className="w-5 h-5 text-black" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold text-white">
                {isGeneralMode ? t('askSovereign') : t('technicalAnalysisChat')}
              </SheetTitle>
              <p className="text-xs text-neutral-400 mt-0.5">
                {isGeneralMode ? (
                  <span className="text-cyan-400">{t('generalChat')}</span>
                ) : (
                  <>
                    {ticker && (
                      <span className="font-semibold text-cyan-400 font-mono">
                        ${ticker}
                      </span>
                    )}
                    {ticker && ' • '}
                    <span className={sentimentColor}>
                      {t((sentiment || 'neutral').toLowerCase())} {t('signal')}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {userMessageCount >= 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleNewChat}
                className="text-neutral-400 hover:text-emerald-400 hover:bg-emerald-500/10 h-9 w-9 rounded-full transition-colors"
                title={t('newChat')}
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-9 w-9 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </SheetHeader>

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5"
        >
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-neutral-500 text-sm">
                <div className="w-4 h-4 border-2 border-neutral-600 border-t-emerald-400 rounded-full animate-spin" />
                {t('loadingConversation')}
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300',
                  message.type === 'user' && 'justify-end'
                )}
              >
                {message.type === 'ai' && (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                    <Sparkles className="w-4 h-4 text-black" />
                  </div>
                )}

                <div
                  className={cn(
                    'max-w-[75%] rounded-2xl px-4 py-3.5 text-sm leading-relaxed shadow-md',
                    message.type === 'ai'
                      ? 'bg-neutral-900/80 text-neutral-100 rounded-tl-sm border border-white/[0.06] prose prose-invert prose-sm prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-white max-w-none'
                      : 'bg-gradient-to-br from-emerald-600 to-cyan-600 text-white rounded-tr-sm whitespace-pre-line'
                  )}
                >
                  {message.type === 'ai' ? (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>

                {message.type === 'user' && (
                  <div
                    className={`w-9 h-9 rounded-full bg-gradient-to-br ${userAvatar.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                  >
                    <span className="text-black text-xs font-bold">
                      {userAvatar.initials}
                    </span>
                  </div>
                )}
              </div>
            ))
          )}

          {isTyping && (
            <div className="flex gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              <div className="bg-neutral-900/80 rounded-2xl rounded-tl-sm px-5 py-3.5 border border-white/[0.06] shadow-md">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce [animation-delay:150ms]" />
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conversation limit banner */}
        {isNearConversationLimit && (
          <div
            className={cn(
              'flex-shrink-0 px-4 py-2.5 flex items-center gap-2 text-xs border-t',
              isAtConversationLimit
                ? 'bg-red-500/10 border-red-500/20 text-red-400'
                : 'bg-amber-500/10 border-amber-500/20 text-amber-400'
            )}
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              {isAtConversationLimit
                ? t('conversationLimitReached')
                : t('messagesUsed', {
                    count: userMessageCount,
                    limit: CONVERSATION_MESSAGE_LIMIT,
                  })}
            </span>
          </div>
        )}

        {/* Input Area */}
        <div className="flex-shrink-0 p-5 border-t border-white/[0.06] bg-black/30 space-y-2">
          <div className="flex gap-2.5">
            <Input
              value={input}
              onChange={(e) => {
                if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                  setInput(e.target.value)
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                isAtConversationLimit
                  ? t('conversationLimitPlaceholder')
                  : isGeneralMode
                    ? t('generalChatPlaceholder')
                    : t('insightChatPlaceholder')
              }
              maxLength={MAX_MESSAGE_LENGTH}
              className={cn(
                'flex-1 bg-neutral-900/80 border text-white placeholder:text-neutral-500 h-11 px-4 focus:ring-2 focus:ring-emerald-500/30',
                isAtLimit
                  ? 'border-red-500/40 focus:border-red-500/50'
                  : 'border-white/[0.08] focus:border-emerald-500/50'
              )}
              disabled={isTyping || isLoadingHistory || isAtConversationLimit}
            />
            <Button
              onClick={handleSend}
              disabled={
                !input.trim() ||
                isTyping ||
                isLoadingHistory ||
                isAtConversationLimit
              }
              size="icon"
              className="bg-gradient-to-br from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black disabled:opacity-40 disabled:cursor-not-allowed h-11 w-11 shadow-lg shadow-emerald-500/25"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between px-0.5">
            <p className="text-[11px] text-neutral-500 font-mono">
              {isAtConversationLimit ? '' : t('pressEnterToSend')}
            </p>
            {input.length > 0 && !isAtConversationLimit && (
              <p
                className={cn(
                  'text-[11px] font-mono tabular-nums transition-colors',
                  charCountColor
                )}
              >
                {input.length}/{MAX_MESSAGE_LENGTH}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
